import React, { useState, useEffect } from 'react'
import {
  useGridApiContext,
  useGridSelector,
  gridPageCountSelector,
  gridPageSelector,
  GridPaginationModel,
  useGridRootProps,
  gridRowCountSelector
} from '@mui/x-data-grid'
import {
  Button,
  Box,
  Select,
  MenuItem,
  Typography,
  SelectChangeEvent,
  TextField,
  TypographyProps
} from '@mui/material'
import FirstPageIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import LastPageIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import NavigateBefore from '@mui/icons-material/NavigateBefore'
import NavigateNext from '@mui/icons-material/NavigateNext'

// Custom button styles to remove hover/active effects
const buttonSx = {
  color: '#212121',
  minWidth: 'auto',
  mr: 0,
  '&:hover': {
    backgroundColor: 'transparent'
  },
  '&:active': {
    backgroundColor: 'transparent'
  },
  '&:focus': {
    backgroundColor: 'transparent'
  },
  '&.Mui-disabled': {
    backgroundColor: 'transparent'
  },
  // Remove ripple effect
  '& .MuiTouchRipple-root': {
    display: 'none'
  }
}

const CustomPagination = ({ total }: { total?: number }) => {
  const apiRef = useGridApiContext()
  const rootProps = useGridRootProps()
  const page = useGridSelector(apiRef, gridPageSelector)
  const pageCount = useGridSelector(apiRef, gridPageCountSelector)
  const pageSize = rootProps.paginationModel?.pageSize || 10
  const gridRowCount = useGridSelector(apiRef, gridRowCountSelector)
  const totalRowCount = total ?? gridRowCount

  // State for page input
  const [pageInputValue, setPageInputValue] = useState((page + 1).toString())

  // Update input value when page changes externally
  useEffect(() => {
    setPageInputValue((page + 1).toString())
  }, [page])

  // Handle go to first page
  const handleFirstPageClick = () => {
    apiRef.current.setPage(0)
  }

  // Handle go to last page
  const handleLastPageClick = () => {
    apiRef.current.setPage(pageCount - 1)
  }

  // Handle page size change
  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newPageSize = Number(event.target.value)
    apiRef.current.setPaginationModel({
      page: 0,
      pageSize: newPageSize
    } as GridPaginationModel)
  }

  // Handle next page
  const handleNextPage = () => {
    apiRef.current.setPage(page + 1)
  }

  // Handle previous page
  const handlePrevPage = () => {
    apiRef.current.setPage(page - 1)
  }

  // Handle page input change
  const handlePageInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Only allow numbers
    const value = event.target.value.replace(/[^0-9]/g, '')
    setPageInputValue(value)
  }

  // Handle page input submit
  const handlePageInputSubmit = () => {
    let newPage = parseInt(pageInputValue, 10)

    // Validate the page number
    if (isNaN(newPage) || newPage < 1) {
      newPage = 1
    } else if (newPage > pageCount) {
      newPage = pageCount
    }

    // Update page number
    apiRef.current.setPage(newPage - 1)

    // Update the input value to match actual page
    setPageInputValue(newPage.toString())
  }

  // Handle key press in page input
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handlePageInputSubmit()
    }
  }

  // Handle blur event
  const handleBlur = () => {
    handlePageInputSubmit()
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
        // px: 2
      }}>
      {/* Left side - total count */}
      <Typography
        variant={'body7' as TypographyProps['variant']}
        sx={{ pr: 1 }}>
        {totalRowCount} records
      </Typography>

      {/* Center - page navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Button
          onClick={handleFirstPageClick}
          disabled={page === 0}
          startIcon={<FirstPageIcon />}
          size='small'
          disableRipple
          sx={buttonSx}
        />
        <Button
          onClick={handlePrevPage}
          disabled={page === 0}
          size='small'
          disableRipple
          sx={{
            ...buttonSx,
            minWidth: 20
          }}>
          <NavigateBefore />
        </Button>

        {/* Always show TextField for page number */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            value={pageInputValue}
            onChange={handlePageInputChange}
            onKeyDown={handleKeyPress}
            onBlur={handleBlur}
            size='small'
            variant='outlined'
            sx={{
              '& input': {
                maxWidth: '70px',
                padding: '4px 1px',
                textAlign: 'center',
                fontSize: '14px'
              },
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '1px'
                },
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.23)'
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.42)'
                }
              }
            }}
          />{' '}
          <Typography variant='body2' sx={{ ml: 1 }}>
            / {pageCount || 1}
          </Typography>
        </Box>

        <Button
          onClick={handleNextPage}
          disabled={page >= pageCount - 1}
          size='small'
          disableRipple
          sx={{
            ...buttonSx,
            minWidth: 20
          }}>
          <NavigateNext />
        </Button>
        <Button
          onClick={handleLastPageClick}
          disabled={page >= pageCount - 1}
          endIcon={<LastPageIcon />}
          size='small'
          disableRipple
          sx={buttonSx}
        />
      </Box>

      {/* Right side - page size selector */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Select
          value={pageSize}
          onChange={handlePageSizeChange}
          size='small'
          variant='outlined'
          sx={{ height: 28, mx: 1, fontSize: '14px' }}>
          {(rootProps.pageSizeOptions || []).map(option => {
            // Ensure option is a simple number
            const numericOption = Number(option)
            return (
              <MenuItem key={numericOption} value={numericOption}>
                {numericOption} / page
              </MenuItem>
            )
          })}
        </Select>
      </Box>
    </Box>
  )
}

export default CustomPagination
